// Client mirror of the SocialKit Composition document
// (apps/socialkit/src/core/visual/v2/composition.ts). Field names MUST stay
// identical to the worker so the document round-trips byte-for-byte. The pixel
// parity test (composition.parity.spec) guards that the client DOM render matches
// the worker satori master. (Follow-up: extract a shared @taka/composition package
// — see QUESTIONS.md.)

export const COMPOSITION_VERSION = 1 as const

export type Hex = string
export type AspectRatio = '4:5' | '9:16' | '1:1' | '2:3'

export type LayerBox = {
  x: number
  y: number
  w: number
  h: number
  rotation?: number
}

export type LayerRole =
  | 'background'
  | 'grade'
  | 'scrim'
  | 'kicker'
  | 'headline'
  | 'subhead'
  | 'accent'
  | 'decor'
  | 'baseline'
  | 'logo'
  // A customer's own pixels on a testimonial post (a bubble crop); mirror of the worker.
  | 'proof'

// The narrative role of a slide within a carousel deck (mirror of the worker's SlideRole).
export type SlideRole = 'hook' | 'rehook' | 'body' | 'cta'

export type LayerBase = LayerBox & {
  id: string
  z: number
  name?: string
  opacity?: number
  locked?: boolean
  hidden?: boolean
  seeded?: boolean
  /** Engine role of a seeded layer; lets an AI revise update content by role. */
  role?: LayerRole
  /** True once the user manually edited/added this layer — revise preserves its
   *  layout/style (the AI still owns words + imagery). */
  userEdited?: boolean
  /** LEGACY (pre-N1) video field: show window within the beat (beat-relative seconds).
   *  normalizeVideoComposition (video-time.ts) lifts it onto the owning VideoLayerClip's
   *  absolute window and drops it. Never written by N1+ code; ignored by static/carousel. */
  timing?: { start: number; end: number }
  /** VIDEO layers only: entrance/exit presets fired at the owning clip's window edges
   *  (CSS live preview, burned on render). */
  anim?: { in?: TextAnimPreset; out?: TextAnimPreset }
}

export type SolidFill = { kind: 'solid'; color: Hex }
export type GradientFill = { kind: 'gradient'; from: Hex; to: Hex; angleDeg?: number }
export type RgbaFill = { kind: 'rgba'; color: string }
export type LinearGradientFill = {
  kind: 'linear'
  angleDeg: number
  stops: { color: string; atPct: number }[]
}
export type Fill = SolidFill | GradientFill | RgbaFill | LinearGradientFill

export type ImageSrc =
  | { kind: 'media'; mediaId: string }
  | { kind: 'url'; url: string }
  | { kind: 'dataUri'; dataUri: string }
  // A Taka-server GCS attachment, referenced by its stable id. The signed url it
  // points at expires (~6h), so we persist the id and the SERVER re-signs it to a
  // fresh { kind:'url' } before any worker render/score. The client resolves it to a
  // display url on the fly (brand-kit store / upload cache / download-url endpoint).
  | { kind: 'attachment'; attachmentId: string }

export type TextAlign = 'left' | 'center' | 'right'
export type TextDirection = 'ltr' | 'rtl' | 'auto'

export type TextRun = {
  text: string
  color?: Hex
  fontSizePct?: number
  fontWeight?: number
  fontFamily?: string
  // When true, this run flows INLINE after the previous run (same line) for rich-text
  // emphasis within a line. When absent/false it starts a NEW line. Absent on every legacy
  // run ⇒ each run is its own line ⇒ unchanged render (see groupRunsIntoLines). Keep in sync
  // with the worker's TextRun (apps/socialkit/src/core/visual/v2/composition.ts).
  inline?: boolean
  // Typography treatments (TypeSpec emphasis styles, editable per run). All optional ⇒
  // absent on legacy runs ⇒ byte-identical render. The *Color fields carry the PEN: their
  // presence turns the treatment on, their value is directly recolorable in the editor.
  italic?: boolean
  underlineColor?: Hex
  highlightColor?: Hex
  encircleColor?: Hex
}

export type TextLayer = LayerBase & {
  type: 'text'
  runs: TextRun[]
  fontFamily: string
  fontWeight: number
  fontSizePct: number
  color: Hex
  align: TextAlign
  lineHeight: number
  letterSpacingPct?: number
  direction: TextDirection
  uppercase?: boolean
}

/** Entrance/exit animation presets for timed video text. */
export type TextAnimPreset = 'fade' | 'slide' | 'pop'

export type ImageLayer = LayerBase & {
  type: 'image'
  src: ImageSrc
  objectFit: 'cover' | 'contain'
  focalX?: number
  focalY?: number
  radiusPct?: number
  /** Set when the user explicitly swapped the photo, so an AI revise keeps it. */
  srcUserChosen?: boolean
}

export type OverlayLayer = LayerBase & {
  type: 'overlay'
  fill: Fill
}

export type LogoLayer = LayerBase & {
  type: 'logo'
  src: ImageSrc
  /** Set when the user explicitly swapped the logo, so an AI revise keeps it. */
  srcUserChosen?: boolean
}

export type ShapeKind = 'rect' | 'pill' | 'line' | 'ellipse'

export type ShapeLayer = LayerBase & {
  type: 'shape'
  shapeKind: ShapeKind
  fill: Fill
  radiusPct?: number
}

/** A hand-made accent mark (worker decor.ts mirror): a stroke SVG regenerated from
 *  mark + color at the layer's box size — declarative, so recolor/resize stays crisp. */
export type DecorLayerMark = 'squiggle' | 'zigzag' | 'asterisk' | 'arrow' | 'circle'
export type DecorLayer = LayerBase & {
  type: 'decor'
  mark: DecorLayerMark
  color: Hex
}

export type Layer = TextLayer | ImageLayer | OverlayLayer | LogoLayer | ShapeLayer | DecorLayer
export type LayerType = Layer['type']

export type CompositionCanvasSpec = {
  aspectRatio: AspectRatio
  width: number
  height: number
  background?: Fill
}

export type CompositionBrand = {
  palette: {
    primary: Hex
    secondary?: Hex
    accent?: Hex
    neutralDark?: Hex
    neutralLight?: Hex
  }
  fonts: { display: string; body: string; serif?: string }
  logoSrc?: ImageSrc
}

export type PostComposition = {
  compositionVersion: typeof COMPOSITION_VERSION
  shape: 'static'
  canvas: CompositionCanvasSpec
  brand: CompositionBrand
  layers: Layer[]
  caption: string
  hashtags: string[]
  source: {
    engineId: string
    formatId?: string
    hasSpec: boolean
  }
  meta: {
    seededFromEngine: boolean
    dirty: boolean
  }
  /** Present ONLY when this composition is a slide within a carousel deck. Stable id
   *  (reorder/add/delete + revise-merge identity) + the slide's narrative role. */
  slide?: { id: string; role: SlideRole }
}

// A carousel = an ordered deck of slide compositions (swipe order) + deck-level
// caption/hashtags. Each slide IS a PostComposition, so every per-slide editor tool
// works unchanged; the deck adds slide management + a set-level score.
export type CarouselComposition = {
  compositionVersion: typeof COMPOSITION_VERSION
  shape: 'carousel'
  slides: PostComposition[]
  caption: string
  hashtags: string[]
  source: {
    engineId: string
    hasSpec: boolean
    arc?: string
  }
  meta: {
    seededFromEngine: boolean
    dirty: boolean
  }
}

// --- Video (mirror of the worker's video composition types) --------------
// A video = a TIMELINE of beats; each beat's on-screen text is an editable PostComposition
// on a transparent canvas over a text-free background. Reuses the entire static editor per
// beat, exactly like a carousel slide. See composition.ts for the full contract.

export type VideoBeatRole = 'hook' | 'value' | 'turn' | 'cta'
export type VideoShotKind = 't2v' | 'i2v'

// Auto word-timed caption (machine-owned, re-derived from VO on render; NOT an editable layer).
export type BeatCaption = { text: string; start: number; end: number }

// A beat's TEXT-FREE background (discriminated: clip / real photo / plain brand card).
export type BeatBackground =
  | {
      kind: 'video'
      candidates?: ImageSrc[]
      clipSrc?: ImageSrc
      posterSrc?: ImageSrc
      sceneIdea?: string
      shotKind: VideoShotKind
      /** Blueprint state: planned scene, no clip generated yet (cleared when a clip lands). */
      planned?: boolean
    }
  // Photo-first direction (I14): focalX/focalY = the 0..1 focal point the 9:16 cover-crop
  // centers on; punch = the zoom-push level the master burns ('hold' subtle / 'push' classic
  // / 'hard' tight crop around the focal point). Previewed as object-position + a subtle scale.
  | {
      kind: 'photo'
      photoSrc: ImageSrc
      focalX?: number
      focalY?: number
      punch?: 'hold' | 'push' | 'hard'
      sceneIdea?: string
    }
  | { kind: 'card'; background: Fill; textureSrc?: ImageSrc }

export type VideoBeatComposition = {
  id: string
  role: VideoBeatRole
  /** Editable on-screen text as a transparent-canvas PostComposition (reuse the static editor). */
  overlay: PostComposition
  background: BeatBackground
  vo: string
  voSrc?: ImageSrc | null
  /** Set when the user edits `vo` → re-synth on the next explicit render. */
  voDirty?: boolean
  /** Auto word-timed captions (preview-only; re-derived from voSrc on render). */
  captions?: BeatCaption[]
  durationSec: number
  /** How this beat ENTERS from the previous one: absent/'cut' = hard cut, 'crossfade' = ~0.4s
   *  dissolve (CSS live preview, ffmpeg xfade in the master). Ignored on the first beat. */
  transitionIn?: 'cut' | 'crossfade'
  seeded?: boolean
  userEdited?: boolean
}

/** One FREE LAYER CLIP on the reel timeline (N1, mirror of the worker's VideoLayerClip):
 *  a layer + its ABSOLUTE show window in EFFECTIVE (master) seconds — crossfade overlaps
 *  subtracted, so timeline time = mp4 time (see video-time.ts). Clips are not attached to
 *  beats: inside one beat, spanning several, or riding the whole reel are all the same
 *  thing. `anim` in/out fire at the window edges; stacking is by `layer.z`. */
export type VideoLayerClip = { layer: Layer; start: number; end: number }

export type VideoComposition = {
  compositionVersion: typeof COMPOSITION_VERSION
  shape: 'video'
  canvas: CompositionCanvasSpec
  brand: CompositionBrand
  beats: VideoBeatComposition[]
  caption: string
  hashtags: string[]
  cta: string
  /** Where word-timed captions sit in-frame (deck-level). Defaults to 'bottom'. */
  captionPlacement?: 'top' | 'center' | 'bottom'
  /** How the captions look (deck-level): pop (bold caps, default) / clean (minimal) / block (accent box). */
  captionStyle?: 'pop' | 'clean' | 'block'
  /** Deck-level caption switch: false = no word-timed captions in preview or render. Absent/true = on. */
  captionsEnabled?: boolean
  /** Deck-level voiceover switch: false = scripts kept but the reel previews/renders silent. Absent/true = on. */
  voEnabled?: boolean
  /** The ElevenLabs voice for the whole reel. Absent = engine default. Changing it re-records every beat. */
  voVoiceId?: string
  music?: { src?: ImageSrc | null; enabled: boolean } | null
  /** N1: EVERY visual layer as a free clip on absolute effective reel time. Present (even
   *  if empty) ⇔ normalized; normalizeVideoComposition lifts legacy per-beat overlay
   *  layers + `pinned` into this on read. */
  layers?: VideoLayerClip[]
  /** LEGACY (pre-N1 pinned reel-level layers). Migrated into `layers` on read. */
  pinned?: Array<{ layer: Layer; start: number; end: number }>
  source: {
    engineId: string
    hasSpec: boolean
    arc?: string
  }
  meta: {
    seededFromEngine: boolean
    dirty: boolean
    renderedMasterId?: string
    renderedSig?: string
  }
}

export type Composition = PostComposition | CarouselComposition | VideoComposition

export function isCarouselComposition(c: Composition): c is CarouselComposition {
  return c.shape === 'carousel'
}
/**
 * The server synthesizes a one-layer-per-slide deck from the slide masters when a carousel
 * has no stored composition yet (legacy decks, and the seconds between a fresh deck's
 * masters landing and its layered document being persisted). It is a stand-in, never the
 * document the engine built: a composer that loads one keeps looking for the real deck.
 */
export const BACKFILL_ENGINE_ID = 'backfill-media-items'
export function isBackfilledComposition(c: Composition | null | undefined): boolean {
  return !!c && c.source?.engineId === BACKFILL_ENGINE_ID
}
export function isStaticComposition(c: Composition): c is PostComposition {
  return c.shape === 'static'
}
export function isVideoComposition(c: Composition): c is VideoComposition {
  return c.shape === 'video'
}
